import { chain, isEqual } from 'lodash'
import flatten from 'lodash/flatten'
import * as React from 'react'

import { IMonthsRange } from 'main/gql/schoolAgeGroup'
import { ITuitionBillingPolicy } from 'main/gql/schoolTuitionBillingPolicy'
import { ITuitionProgram } from 'main/gql/schoolTuitionProgram'
import { TabGroup } from 'main/ui'
import { formatAgeRange } from 'main/util/age'
import { tuitionProgramAgeRange } from 'main/util/schoolTuitionProgram'
import { tabOptionFromValue } from 'main/util/tab'
import DetailedPricingTable from './DetailedPricingTable'
import PaymentFrequency, { PaymentFrequencyOption } from './PaymentFrequency'

import { DetailedPricingTableContainer } from './DetailedPricing.styles'

interface IProps {
  tuitionPrograms: ITuitionProgram[]
  tuitionBillingPolicy: ITuitionBillingPolicy
}

export default function DetailedPricing(props: IProps) {
  const { tuitionPrograms, tuitionBillingPolicy } = props

  const hasWeeklyPricesDisplayed = flatten(
    tuitionPrograms.map(program => program.priceOptions),
  ).some(priceOption => priceOption.weeklyRate !== null && !priceOption.hidden)

  const initialPaymentFrequency =
    tuitionBillingPolicy.weeklyBillingAccepted && hasWeeklyPricesDisplayed
      ? PaymentFrequencyOption.weekly
      : PaymentFrequencyOption.monthly

  const [paymentFrequency, setPaymentFrequency] =
    React.useState<PaymentFrequencyOption>(initialPaymentFrequency)

  const ageRangeOptions = chain(tuitionPrograms)
    .map(tuitionProgram =>
      tabOptionFromValue(tuitionProgramAgeRange(tuitionProgram), formatAgeRange),
    )
    .sortBy([option => option.value.min, option => option.value.max])
    .uniqWith(isEqual)
    .value()

  const [selectedAgeRange, setSelectedAgeRange] = React.useState<IMonthsRange | undefined>(
    ageRangeOptions[0] && ageRangeOptions[0].value,
  )
  const visibleTuitionPrograms = tuitionPrograms.filter(tuitionProgram =>
    isEqual(tuitionProgramAgeRange(tuitionProgram), selectedAgeRange),
  )

  return selectedAgeRange ? (
    <>
      <PaymentFrequency
        paymentFrequency={paymentFrequency}
        setPaymentFrequency={setPaymentFrequency}
        weeklyBillingAccepted={tuitionBillingPolicy.weeklyBillingAccepted}
        hasWeeklyPricesDisplayed={hasWeeklyPricesDisplayed}
      />
      <TabGroup<IMonthsRange>
        outline
        options={ageRangeOptions}
        value={selectedAgeRange}
        onChange={setSelectedAgeRange}
      />
      {visibleTuitionPrograms.map((tuitionProgram, index) => (
        <DetailedPricingTableContainer key={index}>
          <DetailedPricingTable
            tuitionProgram={tuitionProgram}
            paymentFrequency={paymentFrequency}
          />
        </DetailedPricingTableContainer>
      ))}
    </>
  ) : null
}
